import { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check, Loader2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getLanguage } from '../../config/languages';

interface LanguageSelectorProps {
  compact?: boolean;
}

export default function LanguageSelector({ compact = false }: LanguageSelectorProps) {
  const { language, setLanguage, languages, isChangingLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang = getLanguage(language);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleSelect = (code: string) => {
    setLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !isChangingLanguage && setIsOpen(!isOpen)}
        disabled={isChangingLanguage}
        className={`flex items-center gap-1.5 text-gray-600 hover:text-primary-600 transition-colors ${
          compact ? 'p-1.5' : 'px-2 py-1.5'
        } rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-wait`}
        aria-label={t('languageSelector.selectLanguage', 'Select language')}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {isChangingLanguage ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Globe className="w-4 h-4" />
        )}
        {!compact && (
          <>
            <span className="text-sm font-medium">{currentLang?.nativeName || language.toUpperCase()}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-56 max-h-80 overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          role="listbox"
          aria-label={t('languageSelector.availableLanguages', 'Available languages')}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleSelect(lang.code)}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 ${
                language === lang.code ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
              }`}
              role="option"
              aria-selected={language === lang.code}
              dir={lang.rtl ? 'rtl' : 'ltr'}
            >
              <span className="flex items-center gap-2">
                <span className="font-medium">{lang.nativeName}</span>
                {lang.nativeName !== lang.name && (
                  <span className="text-gray-400">({lang.name})</span>
                )}
              </span>
              {language === lang.code && (
                <Check className="w-4 h-4 text-primary-600" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
